import { useContext, useEffect, useRef, useState, type RefObject } from "react";
import type { CharacterBubble2Ref } from "src/components/CharacterViewer/CharacterBubble2";
import { Box, Button, Container, Fab, IconButton, LinearProgress, Snackbar, Typography, useTheme, useMediaQuery, Fade } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { WidgetContext } from "src/contexts/WidgetContext";
import { useAuth0 } from "@auth0/auth0-react";
import SourceStories from "../SourceStories/SourceStories";
import ThumbsResponse from "../ThumbsResponse/ThumbsResponse";
import { useTypewriter } from "src/utils/useTypewriter";
import ChartBar from "../Charts/ChartBar";
// import ThoughtItem from "../ThoughtItem/ToughtItem";
import ChartLine from "../Charts/ChartLine";
import ChartScatter from "../Charts/ChartScatter";
import ChartStackBar from "../Charts/ChartStackBar";
import { isPlatform } from "@ionic/react";
import SuspenseLoader from "../SuspenseLoader/SuspenseLoader";
import { audioService, AudioPriority } from "src/services/audioService";
import { isCapacitorNative, prefetchVegaFromAudioUrl, prepareThenApplyVegaFromAudioUrl, prepareVegaHeadFromAudioUrl, subscribeVegaUrlUpdates, tryApplyVegaFromAudioUrlSync } from "src/lipsync/vegaLipSync";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { useHandleShare } from "src/utils/handleShare";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import { frontEnvNumber, subscribeFrontRuntimeConfig } from "src/services/runtimeFrontConfig";
import UpgradeSubscriptionFree from "../PaymentMethod/UpgradeSubscriptionFree";
import UpgradeSubscriptionNonLogged from "../PaymentMethod/UpgradeSubscriptionNonLogged";
import SourceIcon from "../SourceStories/SourceIcon";
import CreditsValueButtons from "../CreditsUi/CreditsValueButtons";
// Helper component to render the correct chart based on its type
const ChartRenderer = ({ chartData }) => {
  if (!chartData) return null;

  switch (chartData.chart_type) {
    case "line":
      return <ChartLine chartData={chartData} />;
    case "bar":
      return <ChartBar chartData={chartData} />;
    case "stacked_bar":
      return <ChartStackBar chartData={chartData} />;
    case "scatter":
      return <ChartScatter chartData={chartData} />;
    default:
      // Return null or a fallback component if chart_type is unknown
      return null;
  }
};

function TradeResponseStream({
  handleTradeResponseOpen,
  isLoading,
  setIsLoading,
  goBack,
  onCloseModal,
  tradeResponseData,
  userDataPlatform,
  givePlayers,
  receivePlayers,
  setShouldPlayAudio,
  isGeneratingResult,
  isWaitingForChart,
  tradeAudioUrl,
  hasLiveTradeAudio, // NEW: indicates if live PCM audio has already been played
  setIsPaymentOpen,
  setInitialPaymentView,
  isExpanded,
  onToggleExpand,
  characterRef,
  creditsBoxRef,
  isCreditsAnimating,
}: {
  handleTradeResponseOpen: () => void;
  isLoading: boolean;
  setIsLoading: (v: boolean) => void;
  goBack: () => void;
  onCloseModal: () => void;
  tradeResponseData: any;
  userDataPlatform: any;
  givePlayers: any[];
  receivePlayers: any[];
  setShouldPlayAudio: (v: boolean) => void;
  isGeneratingResult: boolean;
  isWaitingForChart: boolean;
  tradeAudioUrl: string | null;
  hasLiveTradeAudio: boolean;
  setIsPaymentOpen: (open: boolean) => void;
  setInitialPaymentView: (view: string) => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  characterRef?: RefObject<CharacterBubble2Ref | null>;
  creditsBoxRef: RefObject<HTMLDivElement>;
  isCreditsAnimating: boolean;
}) {
  const [, setFrontRuntimeBump] = useState(0);
  useEffect(() => subscribeFrontRuntimeConfig(() => setFrontRuntimeBump((n) => n + 1)), []);
  const agentEndpointCredits = frontEnvNumber("REACT_APP_AGENT_ENDPOINT_CREDITS", 10);

  const [showingNewResponse, setShowingNewResponse] = useState(false);
  const containerRef = useRef(null);
  const { userIdToken, clientIdToken, selectedTeamInfo, NON_LOGGED_IN_CONFIG, isLoggedInAuth0ExperienceGlobal, aiSettings, isDrawerOpen } = useContext(WidgetContext);
  const { getAccessTokenSilently } = useAuth0();
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
  const { handleShare, isCheckmark } = useHandleShare();

  // Only show UpgradeSubscriptionFree if this response was generated with FREE model
  const wasFreeModel = tradeResponseData?.model === "RotoGPT FREE";

  //Get the auth0 user id for the thumbs response typeform
  const { user } = useAuth0();
  const authId = user?.sub || NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_AUTH_ID;
  // Add detection of mobile/tablet device
  const iosOrAndroid = isPlatform("hybrid");

  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg")) && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  //get the client id and user id from the context
  const client_id = clientIdToken;
  const user_id = userDataPlatform.user_id;

  // --- Audio Handling Logic (replicated from HomePage.tsx) ---
  const tradeAudioRef = useRef<HTMLAudioElement | null>(null);
  const hasAudioBeenPlayed = useRef(false);

  // When parent clears URL (new analysis), allow autoplay again for the next URL (same trade from cache).
  useEffect(() => {
    if (!tradeAudioUrl) {
      hasAudioBeenPlayed.current = false;
    }
  }, [tradeAudioUrl]);

  useEffect(() => {
    prefetchVegaFromAudioUrl(tradeAudioUrl);
  }, [tradeAudioUrl]);

  // Effect to autoplay the trade audio summary when it becomes available
  useEffect(() => {
    // Guard clauses: do nothing if no audio, or if it has already played, or if live PCM audio is already playing.
    if (!tradeAudioUrl || hasAudioBeenPlayed.current || hasLiveTradeAudio || !isDrawerOpen) {
      return;
    }

    // const audioUrl = URL.createObjectURL(tradeAudioBlob);

    // const newAudio = new Audio(audioUrl);
    const newAudio = new Audio(tradeAudioUrl);
    newAudio.setAttribute("playsinline", "true");
    tradeAudioRef.current = newAudio;

    let stopVegaUpdates = () => {};
    let cancelled = false;
    void (async () => {
      // Capacitor: same as agent cache URL — play when the first ~2s of visemes exist.
      if (isCapacitorNative()) {
        const headReady = await prepareVegaHeadFromAudioUrl(tradeAudioUrl);
        if (cancelled || !headReady || !characterRef?.current) return;
        tryApplyVegaFromAudioUrlSync(tradeAudioUrl, characterRef.current, "trade-url");
        characterRef.current.setAudioElement(newAudio);
        const playedNow = audioService.playAudio(newAudio, AudioPriority.HIGH);
        const actualPlayingAudio = audioService.getNowPlayingAudio() ?? newAudio;
        characterRef.current.setAudioElement(actualPlayingAudio);
        characterRef.current.setIsTalking(true);
        if (playedNow) {
          hasAudioBeenPlayed.current = true;
          actualPlayingAudio.onended = () => {
            if (characterRef?.current) characterRef.current.setIsTalking(false);
          };
        } else {
          characterRef.current.setIsTalking(false);
        }
        stopVegaUpdates = subscribeVegaUrlUpdates(tradeAudioUrl, (done) => {
          if (cancelled || !characterRef?.current) {
            stopVegaUpdates();
            return;
          }
          tryApplyVegaFromAudioUrlSync(tradeAudioUrl, characterRef.current, "trade-url");
          if (done) stopVegaUpdates();
        });
        return;
      }

      await prepareThenApplyVegaFromAudioUrl(
        tradeAudioUrl,
        characterRef?.current,
        "trade-url",
        () => {
          audioService.stopCurrentAudio();
          characterRef?.current?.setIsTalking(false);
        },
      );
      if (cancelled) return;

      // Capacitor Vega: bind before play so the heard clock starts with the first sample (welcome-style).
      if (isCapacitorNative() && characterRef?.current) characterRef.current.setAudioElement(newAudio);

      // Use the service to play the audio with HIGH priority
      const playedSuccessfully = audioService.playAudio(newAudio, AudioPriority.HIGH);

      // On mobile web, audioService transfers the src to a shared unlocked player.
      // We must bind CharacterEmbed to the *actual* playing element, not just newAudio.
      const actualPlayingAudio = audioService.getNowPlayingAudio() ?? newAudio;

      if (characterRef?.current) {
        characterRef.current.setAudioElement(actualPlayingAudio);
        characterRef.current.setIsTalking(true);
      }

      if (playedSuccessfully) {
        hasAudioBeenPlayed.current = true; // Mark as played
        // When audio finishes naturally (not aborted)
        actualPlayingAudio.onended = () => {
          if (characterRef?.current) {
            characterRef.current.setIsTalking(false);
          }
        };
      } else {
        if (characterRef?.current) {
          characterRef.current.setIsTalking(false);
        }
      }
    })();

    // Cleanup function to stop the audio when the component unmounts (e.g., modal is closed)
    return () => {
      cancelled = true;
      stopVegaUpdates();
      newAudio.volume = 0;
      newAudio.pause();
      audioService.stopCurrentAudio();
      if (characterRef?.current) characterRef.current.setIsTalking(false);
      // Also revoke the URL on unmount in case it was still playing
      // URL.revokeObjectURL(audioUrl);
    };
  }, [tradeAudioUrl, hasLiveTradeAudio, characterRef, isDrawerOpen]);

  const scrollToTop = () => {
    if (containerRef.current) {
      setTimeout(() => {
        containerRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 50);
    }
  };

  // console.log("tradeResponseData", tradeResponseData);
  // console.log("client_id", client_id);
  // console.log("user_id", user_id);
  // console.log("tradeResponsemessageIdData", tradeResponseData?.messageId);

  //this useEffect is to give time to the fade exterior in father component
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setShowingNewResponse(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  //function to format the player names
  const formatPlayerNames = (players) => {
    //if no players, return empty string
    if (!players || players.length === 0) return "";
    //if there is one player, return the name
    if (players.length === 1) return players[0].name;
    //if there are two players, return the names separated by "and"
    if (players.length === 2) return `${players[0].name} and ${players[1].name}`;
    //if there are more than two players, return the names separated by commas and "and" before the last name
    const allButLast = players
      .slice(0, -1)
      .map((player) => player.name)
      .join(", ");
    const lastPlayer = players[players.length - 1].name;
    return `${allButLast} and ${lastPlayer}`;
  };

  const tradeQuestionMessage = `Should I trade ${formatPlayerNames(givePlayers)} for ${formatPlayerNames(receivePlayers)}?`;

  // analysisText is the text that is displayed in the trade response stream
  const analysisText = tradeResponseData?.trade_analysis || "";
  // chartsData is now an array of chart objects
  const chartsData = tradeResponseData?.chart_data || [];
  const shareUrl = tradeResponseData?.share_url || "";
  // typewriterSpeed is 0 for ios and android, 1 for desktop
  const typewriterSpeed = iosOrAndroid ? 0 : 1;
  // displayedAnalysis is the text that is displayed with the typewriter effect
  const displayedAnalysis = useTypewriter(analysisText, typewriterSpeed);

  // isTypingFinished is a boolean that indicates if the typing is finished for the entire response
  const isTypingFinished = !isLoading && analysisText.length > 0 && displayedAnalysis.length === analysisText.length;

  // Check if the typewriter has finished rendering all text content received *so far*.
  // This is true when the typewriter is "caught up" with the full string from the backend.
  const hasTypewriterFinishedCurrentText = displayedAnalysis.length === analysisText.length;

  // --- Logic to prevent partial table rendering ---
  let textForMarkdown = displayedAnalysis;
  let isStreamingTable = false;
  if (!isTypingFinished) {
    const lastBlockIndex = displayedAnalysis.lastIndexOf("\n\n");
    // Check if the last part of the text seems to be a table
    if (lastBlockIndex !== -1) {
      const lastBlock = displayedAnalysis.substring(lastBlockIndex + 2);
      if (lastBlock.includes("|")) {
        isStreamingTable = true;
        // Only show the text that came before the table
        textForMarkdown = displayedAnalysis.substring(0, lastBlockIndex);
      }
    } else if (displayedAnalysis.includes("|")) {
      // Handles case where the table is at the very beginning of the response
      isStreamingTable = true;
      textForMarkdown = "";
    }
  }
  // --- End of logic ---

  // This regex splits the text by any chart placeholder, e.g., [chart_0]
  // The result is an array of text segments that were between the placeholders.
  const textSegments = textForMarkdown.split(/\[CHART:.*?\]|\[chart_\d+\]/gi); // NEW: Split by the new placeholder format
  // const textSegments = textForMarkdown.split(/\[CHART:.*?\]/gi); // OLD

  // NEW: Find all chart placeholders in the final analysis text to determine their order.
  const chartPlaceholders = analysisText.match(/\[CHART:.*?\]|\[chart_\d+\]/gi) || [];
  // const chartPlaceholders = analysisText.match(/\[CHART:.*?\]/g) || [];

  // console.log("traderesponseData", tradeResponseData);
  return (
    <>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "black",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {/* Header container */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: "black",
          }}
        >
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={1}>
            <IconButton aria-label="close" onClick={goBack}>
              <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
            </IconButton>
            <Typography variant="h4" color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} textAlign={"center"} className={styles.tradeTeam}>
              Trade response
            </Typography>
            <Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center">
              {onToggleExpand && aiSettings.quan && (
                <IconButton aria-label="toggle-expand" onClick={onToggleExpand}>
                  {isExpanded ? (
                    <KeyboardArrowDownIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
                  ) : (
                    <KeyboardArrowUpIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
                  )}
                </IconButton>
              )}
              <IconButton aria-label="close" onClick={onCloseModal}>
                <CloseIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Content container */}
        <div
          ref={containerRef}
          style={{
            flex: 1,
            position: "relative",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            scrollBehavior: "smooth",
            backgroundColor: "black",
            display: "flex",
            flexDirection: "column",
            paddingBottom: "50px",
          }}
          className={styles.noScrollbar}
        >
          <Fade in={true} timeout={500}>
            <Container
              maxWidth="lg"
              sx={{
                p: 0,
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >

              {/* {/* This is the part that shows the status and thought */}
              {/* {!isGeneratingResult ? (
                <>
                  {/* {streamStatus && <Typography sx={{ color: "grey", fontStyle: "italic", mb: 2, textAlign: "center" }}>{streamStatus}</Typography>}
                  {streamThought && <ThoughtItem text={streamThought} />} */}
              {/* {streamThought ? (
                    <ThoughtItem text={streamThought} />
                  ) : ( */}
              {/* {streamThought ? (
                    <ThoughtItem text={streamThought} />
                  ) : streamStatus === "Retrieving your trade analysis..." ? ( // if the stream status is "Retrieving your trade analysis...", show the loading spinner
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={30} disableShrink thickness={3} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} />
                    </Box>
                  ) : (
                    streamStatus && <Typography sx={{ color: "grey", fontStyle: "italic", mb: 2, textAlign: "center" }}>{streamStatus}</Typography>
                  )}
                </>
              ) : ( */}
              {/* Status/thoughts: shown on character sign (thoughtsCommon); not duplicated here */}
              {!isGeneratingResult ? null : (
                <>
                  {/* Render the first segment of text (the part before any charts) */}
                  <Typography variant="h4" fontSize={"16px"} color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} p={1} pb={0}>
                    <ReactMarkdown className={styles.reactMarkdownStart} remarkPlugins={[remarkGfm]}>
                      {textSegments[0]}
                    </ReactMarkdown>
                  </Typography>

                  {/* Loop through ALL placeholders found in the text, not just charts in chartsData */}
                  {/* This ensures text segments after placeholders are always rendered, even if chart_data wasn't received */}
                  {chartPlaceholders.map((chartPlaceholder, index) => {
                    // Check if the typewriter has already passed this chart's placeholder
                    const placeholderPosition = analysisText.indexOf(chartPlaceholder);
                    const currentTypewriterPosition = displayedAnalysis.length;

                    // Only show content if the typewriter has passed this placeholder position
                    const shouldShowContent = placeholderPosition !== -1 && currentTypewriterPosition > placeholderPosition;

                    if (!shouldShowContent) {
                      return null;
                    }

                    // Check if we have actual chart data for this placeholder
                    const chart = chartsData[index];

                    // Text that comes after the chart (can be undefined and is fine)
                    const nextTextSegment = textSegments[index + 1];

                    // Check if we should show loader after this chart
                    // Show loader if: there's no immediate next text segment AND we haven't finished typing AND we're waiting for a chart
                    const shouldShowLoaderAfterChart = !nextTextSegment && !isTypingFinished && isWaitingForChart && !chart;

                    return (
                      <Box key={`chart-block-${index}`}>
                        {/* Only render the chart if we have data for it */}
                        {chart && (
                          <Box sx={{ py: 1 }}>
                            <ChartRenderer chartData={chart} />
                          </Box>
                        )}

                        {/* Show loader after placeholder if no immediate content and still streaming/waiting for chart */}
                        {shouldShowLoaderAfterChart && (
                          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            {/* <CircularProgress size={24} disableShrink thickness={3} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} /> */}
                            <Typography sx={{ color: "grey", fontStyle: "italic", textAlign: "center" }}>Thinking...</Typography>
                          </Box>
                        )}

                        {/* Always render the next text segment if it exists */}
                        {nextTextSegment && (
                          <Typography variant="h4" fontSize={"16px"} color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} p={1} pb={0}>
                            <ReactMarkdown className={styles.reactMarkdownStart} remarkPlugins={[remarkGfm]}>
                              {nextTextSegment}
                            </ReactMarkdown>
                          </Typography>
                        )}
                      </Box>
                    );
                  })}

                  {isStreamingTable && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <Typography sx={{ color: "grey", fontStyle: "italic", textAlign: "center" }}>Generating table...</Typography>
                    </Box>
                  )}

                  {/* Show loader while waiting for a chart to be generated, but only after the preceding text has finished typing. */}
                  {isWaitingForChart && hasTypewriterFinishedCurrentText && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      {/* <CircularProgress size={24} disableShrink thickness={3} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} /> */}
                      <Typography sx={{ color: "grey", fontStyle: "italic", textAlign: "center" }}>Generating chart...</Typography>
                    </Box>
                  )}
                </>
              )}

              {/* Thumbs share and run another trade button  */}
              {isTypingFinished && (
                <Box sx={{ width: "100%", bgcolor: "#000000", mt: 1, mb: 0 }}>
                  <Box sx={{ width: "100%", bgcolor: "#000000", mt: 0, mb: 0 }}>
                    {/* <SourceStories customPaddingBottom={1} customPaddingTop={0.5} /> */}
                    {/* Source icon */}
                    <SourceIcon customPaddingBottom={0.5} customPaddingTop={0.5} />
                  </Box>
                  {/* Only show upgrade button if this response was generated with FREE model */}
                  {wasFreeModel && (
                    isLoggedInAuth0ExperienceGlobal ? (
                      <UpgradeSubscriptionFree setIsPaymentOpen={setIsPaymentOpen} setInitialPaymentView={setInitialPaymentView} />
                    ) : (
                      <UpgradeSubscriptionNonLogged setIsPaymentOpen={setIsPaymentOpen} setInitialPaymentView={setInitialPaymentView} />
                    )
                  )}
                  <ThumbsResponse
                    messageId={tradeResponseData?.messageId}
                    userId={authId}
                    message={tradeQuestionMessage}
                    response={analysisText}
                    handleShare={() => handleShare(tradeResponseData?.messageId, "Check this out this trade!", tradeAudioUrl, shareUrl)}
                    isCheckmark={isCheckmark}
                    section="trade"
                    platformId={selectedTeamInfo?.platformId}
                    leagueId={selectedTeamInfo?.leagueId}
                    teamName={selectedTeamInfo?.teamName}
                    shareUrl={shareUrl}
                  />

                </Box>
              )}
            </Container>
          </Fade>
        </div>

        {/* Footer buttons */}
        <div style={{ position: "absolute", bottom: 0, width: "100%", paddingBottom: "env(safe-area-inset-bottom)" }}>
          {/* This is the part that shows the source stories */}
          {/* {isTypingFinished && (
            <Box sx={{ width: "100%", bgcolor: "#000000", mt: 2, mb: 0 }}>
              <SourceStories customPaddingBottom={1} customPaddingTop={1} />
            </Box>
          )} */}

          <Button
            onClick={goBack}
            className={styles.tradeTeamName}
            variant="contained"
            sx={{
              bgcolor: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
              borderRadius: 0,
              width: "100%",
              zIndex: 1010,
              //   mt: "auto",
              // gap: 1,
            }}
          >
            RUN ANOTHER TRADE
            {/* Credits box */}
            {/* <CreditsValueButtons credits={AGENT_ENDPOINT_CREDITS} isCreditsAnimating={isCreditsAnimating} creditsBoxRef={creditsBoxRef} creditBoxSize={14} creditBoxColor={PureLightThemeCaaS.colors.alpha.white[100]} /> */}
          </Button>
        </div>
      </Box>
    </>
  );
}

export default TradeResponseStream;
